import { emptyFunc, notifyStatusChange } from "./helpers/common";
import { v4 as uuidv4 } from "uuid";
import { CallbackFn, GenerateBookHandlerParams, Task, TaskType } from "./types";
import { taskExecutorMap } from "./task-executor-map";
import { expose } from "comlink";
import {
  WORKER_ERROR_MESSAGE_INVALID_TASK_ID,
  WORKER_MESSAGE_TASK_QUEUED_SUCCESSFULLY,
} from "./protocol/messages";
import { sleep } from "../utils/timers";

const WORKER_RUNNER_INTERVAL = 50;

/**
 * Only the following methods are allowed for the task queue
 *     - shift -> to dequeue a task
 *     - push -> to enqueue a task
 */
const TASK_QUEUE: Array<Task> = [];

/**
 * The current running task is initialized globally
 */
let currentRunningTask: Task | undefined;

/**
 * Queues the given task and returns the task ID
 */
const queueTask = async <T = unknown>(
  type: TaskType,
  parameters: T,
  callback: CallbackFn
): Promise<string> => {
  const taskId = uuidv4();
  const parametersParsed = JSON.parse(parameters as unknown as string);
  const task: Task = {
    taskId,
    type,
    parameters: parametersParsed,
    callback,
  };
  TASK_QUEUE.push(task);
  await notifyStatusChange(
    task,
    "queued",
    WORKER_MESSAGE_TASK_QUEUED_SUCCESSFULLY
  );
  return taskId;
};

/**
 * Drops task with matching task ID from the queue
 */
const dropTask = async (taskId: string): Promise<void> => {
  const isCurrentRunningTask = currentRunningTask?.taskId === taskId;

  if (isCurrentRunningTask && currentRunningTask) {
    currentRunningTask.callback = emptyFunc;
    await notifyStatusChange(currentRunningTask, "dropped");
  } else {
    const task = TASK_QUEUE.find((task) => task.taskId === taskId);

    if (!task) {
      throw new Error(WORKER_ERROR_MESSAGE_INVALID_TASK_ID);
    }

    TASK_QUEUE.splice(TASK_QUEUE.indexOf(task), 1);
    await notifyStatusChange(task, "dropped");
  }
};

/**
 * Returns the IDs of the tasks that are currently on queue
 */
const listTasks = async (): Promise<string[]> => {
  return TASK_QUEUE.map((task) => task.taskId);
};

/**
 * Set a new callback function to a task
 */
const setCallbackFn = async (
  taskId: string,
  callback: CallbackFn
): Promise<void> => {
  const task = TASK_QUEUE.find((task) => task.taskId === taskId);

  if (!task) {
    throw new Error(WORKER_ERROR_MESSAGE_INVALID_TASK_ID);
  }

  TASK_QUEUE[TASK_QUEUE.indexOf(task)] = {
    ...task,
    callback,
  };
};

const run = async () => {
  currentRunningTask = TASK_QUEUE.shift();

  if (!currentRunningTask) {
    return;
  }

  await taskExecutorMap[currentRunningTask.type](
    currentRunningTask as Task<GenerateBookHandlerParams>
  );

  // Resets the current running task
  currentRunningTask = undefined;
};

const workerRunner = async () => {
  // eslint-disable-next-line no-constant-condition
  while (true) {
    await run();
    await sleep(WORKER_RUNNER_INTERVAL);
  }
};

const main = async () => {
  workerRunner();
};

main();

// expose api methods
const taskExecutorApi = {
  queueTask,
  dropTask,
  listTasks,
  setCallbackFn,
};

expose(taskExecutorApi);
